import React, { useState, useCallback, useEffect } from 'react';
import { PageHero, Section } from '../components/PageLayout';
import { galerieImages, heroImages } from '../constants/imagePlaceholders';
import SEO from '../components/SEO';
import { motion, AnimatePresence } from 'framer-motion';
import Masonry from 'react-masonry-css';
import ImageViewer from '../components/ImageViewer';

interface GalleryImage {
  id: number;
  src: string;
  alt: string;
  category: string;
}

const GaleriePage: React.FC = () => {
  // État pour filtrer les images par catégorie
  const [activeCategory, setActiveCategory] = useState<string>('all');
  // État pour la visionneuse d'images
  const [selectedImage, setSelectedImage] = useState<GalleryImage | null>(null);
  const [lightboxOpen, setLightboxOpen] = useState<boolean>(false);
  // État pour les animations d'apparition
  const [isLoaded, setIsLoaded] = useState<boolean>(false);

  // Effet pour déclencher l'animation après le chargement de la page
  useEffect(() => {
    setIsLoaded(true);
  }, []);

  // Toutes les catégories disponibles
  const categories = [
    { id: 'all', name: 'Toutes les photos', icon: '📷' },
    { id: 'restaurant', name: 'Restaurant & Terrasse', icon: '🍽️' },
    { id: 'peche', name: 'Étangs & Pêche', icon: '🎣' },
    { id: 'exterieur', name: 'Paysages & Nature', icon: '🌲' }
  ];

  // Filtrer les images selon la catégorie active
  const filteredImages = activeCategory === 'all' 
    ? galerieImages 
    : galerieImages.filter(img => img.category === activeCategory);

  // Configuration pour la galerie en mosaïque - Optimisée pour mobile
  const breakpointColumnsObj = {
    default: 4,
    1100: 3,
    700: 2,
    500: 1
  };

  // Fonction pour ouvrir la visionneuse d'images
  const openLightbox = useCallback((image: GalleryImage) => {
    setSelectedImage(image);
    setLightboxOpen(true);
  }, []);

  // Fonction pour fermer la visionneuse d'images
  const closeLightbox = useCallback(() => {
    setLightboxOpen(false);
    setTimeout(() => {
      setSelectedImage(null);
    }, 300); // Attendre la fin de l'animation
  }, []);

  // Navigation entre les images
  const navigateImage = useCallback((direction: 'prev' | 'next') => {
    if (!selectedImage) return;
    
    const currentIndex = filteredImages.findIndex(img => img.id === selectedImage.id);
    let newIndex;
    
    if (direction === 'prev') {
      newIndex = currentIndex <= 0 ? filteredImages.length - 1 : currentIndex - 1;
    } else {
      newIndex = currentIndex >= filteredImages.length - 1 ? 0 : currentIndex + 1;
    }
    
    setSelectedImage(filteredImages[newIndex]);
  }, [selectedImage, filteredImages]);

  return (
    <div className="font-body">
      <SEO 
        title="Galerie Photos"
        description="Découvrez le Moulin Tala en images : photos du restaurant, de la terrasse, des étangs de pêche, de la nature et des paysages du Morvan."
        canonicalUrl="https://lemoulintala.fr/galerie"
        keywords="photos moulin tala, galerie morvan, images restaurant morvan, photos pêche morvan"
      />

      {/* Hero Banner */}
      <PageHero 
        title="Galerie Photos"
        subtitle="Découvrez notre domaine en images"
        backgroundImage={heroImages.galerie}
      />

      {/* Filtres */}
      <Section background="light" paddingY="12">
        <div className="max-w-4xl mx-auto px-2">
          <h3 className="text-xl font-medium text-center mb-8 text-dark/90">Parcourez notre collection de photos</h3>
          
          {/* Version desktop des filtres */}
          <div className="hidden md:block relative mb-10">
            {/* Barre décorative simplifiée - sans animation */}
            <div className="absolute h-1 bottom-0 left-0 right-0 bg-gray-200 rounded-full">
              <div 
                className="h-full bg-primary rounded-full" 
                style={{ 
                  width: "25%", 
                  position: 'absolute',
                  left: activeCategory === 'all' ? '0%' : 
                        activeCategory === 'restaurant' ? '25%' : 
                        activeCategory === 'peche' ? '50%' : '75%',
                  transition: 'left 0.3s ease'
                }}
              />
            </div>
            
            {/* Menu de navigation stylisé - desktop */}
            <div className="grid grid-cols-4 gap-2">
              {categories.map((category) => (
                <button
                  key={category.id}
                  onClick={() => setActiveCategory(category.id)}
                  className={`group relative py-4 px-3 transition-all focus:outline-none overflow-hidden rounded-lg ${
                    activeCategory === category.id ? 'text-primary' : 'text-dark/70 hover:text-dark'
                  }`}
                >
                  {/* Fond au survol */}
                  <div 
                    className={`absolute inset-0 rounded-lg ${
                      activeCategory === category.id ? 'bg-primary/10' : 'bg-transparent hover:bg-black/5'
                    }`}
                    style={{ transition: 'background-color 0.2s ease' }}
                  />
                  
                  <div className="relative flex flex-col items-center space-y-2 z-10">
                    <span className="text-2xl mb-1">{category.icon}</span>
                    <span className={`text-sm font-medium ${activeCategory === category.id ? 'text-primary' : ''}`}>
                      {category.name}
                    </span>
                    
                    {/* Indicateur sélectionné simplifié */}
                    {activeCategory === category.id && (
                      <div 
                        className="absolute -bottom-2 w-6 h-1 bg-primary rounded-full"
                      />
                    )}
                  </div>
                </button>
              ))}
            </div>
          </div>

          {/* Version mobile des filtres - plus compacte et tactile */}
          <div className="md:hidden mb-8">
            <div className="grid grid-cols-2 gap-2 px-1">
              {categories.map((category) => (
                <motion.button
                  key={category.id}
                  onClick={() => setActiveCategory(category.id)}
                  className={`flex flex-col items-center justify-center py-3 px-2 rounded-lg ${
                    activeCategory === category.id 
                      ? 'bg-primary text-white' 
                      : 'bg-white text-dark/70 shadow-sm'
                  }`}
                  whileTap={{ scale: 0.95 }}
                >
                  <span className="text-2xl mb-1">{category.icon}</span>
                  <span className="text-xs font-medium">{category.name}</span>
                </motion.button>
              ))}
            </div>
          </div>

          {/* Compteur de photos */}
          <div className="flex justify-center mb-8">
            <motion.div 
              className="inline-flex items-center space-x-2 bg-gray-100 text-gray-800 px-3 py-1 rounded-full text-sm"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3 }}
            >
              <span className="font-medium">{filteredImages.length}</span>
              <span>photo{filteredImages.length > 1 ? 's' : ''} {activeCategory !== 'all' && `de ${categories.find(c => c.id === activeCategory)?.name.toLowerCase()}`}</span>
            </motion.div>
          </div>
        </div>
      </Section>

      {/* Galerie d'images */}
      <Section paddingY="12">
        <AnimatePresence mode="wait">
          <motion.div
            key={activeCategory}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.4 }}
            className="px-2"
          >
            <Masonry
              breakpointCols={breakpointColumnsObj}
              className="flex w-auto -ml-2 sm:-ml-4"
              columnClassName="pl-2 sm:pl-4 bg-clip-padding"
            >
              {filteredImages.map((image, index) => (
                <motion.div
                  key={image.id}
                  className="mb-2 sm:mb-4 overflow-hidden rounded-lg shadow-lg cursor-pointer"
                  initial={{ opacity: 0, y: 50 }}
                  animate={{ 
                    opacity: isLoaded ? 1 : 0, 
                    y: isLoaded ? 0 : 50 
                  }}
                  transition={{ 
                    duration: 0.5,
                    delay: Math.min(index * 0.1, 1) // Limiter le délai max à 1s
                  }}
                  onClick={() => openLightbox(image)}
                  whileHover={{ y: -5, transition: { duration: 0.2 } }}
                >
                  <div className="relative overflow-hidden group">
                    <div className="transform transition-transform duration-700 group-hover:scale-110">
                      <img 
                        src={image.src} 
                        alt={image.alt} 
                        className="w-full h-auto object-cover"
                        loading="lazy"
                      />
                    </div>
                    {/* Info overlay - visible sur touch devices par défaut */}
                    <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent md:opacity-0 opacity-100 md:group-hover:opacity-100 transition-opacity duration-300 flex flex-col justify-end p-2 sm:p-4">
                      <span className="text-white font-medium text-sm sm:text-base">{image.alt}</span>
                      <span className="text-white/80 text-xs sm:text-sm">
                        {categories.find(cat => cat.id === image.category)?.name}
                      </span>
                    </div>
                  </div>
                </motion.div>
              ))}
            </Masonry>
          </motion.div>
        </AnimatePresence>
      </Section>

      {/* Texte explicatif */}
      <Section background="light" paddingY="12" textAlign="center">
        <motion.div 
          className="max-w-3xl mx-auto px-4"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.3 }}
        >
          <h2 className="text-2xl sm:text-3xl font-bold mb-6 text-primary font-heading">Notre Domaine en Images</h2>
          <p className="mb-4 text-base sm:text-lg">
            Nous vous invitons à découvrir le Moulin Tala à travers cette sélection de photos. Notre domaine, 
            situé au cœur du Parc Naturel Régional du Morvan, offre un cadre exceptionnel pour la pêche 
            et la restauration.
          </p>
          <p className="text-base sm:text-lg">
            Ces images vous donnent un aperçu de ce qui vous attend : la beauté naturelle de nos étangs, 
            l'ambiance chaleureuse de restaurant, et la richesse des paysages environnants.
          </p>
        </motion.div>
      </Section>

      {/* Visionneuse d'images plein écran - Optimisée pour mobile */}
      <ImageViewer
        isOpen={lightboxOpen}
        onClose={closeLightbox}
        image={selectedImage ? {
          src: selectedImage.src,
          alt: selectedImage.alt,
          title: selectedImage.alt,
          description: categories.find(cat => cat.id === selectedImage.category)?.name
        } : null}
        onPrev={() => navigateImage('prev')}
        onNext={() => navigateImage('next')}
        showNavigation={true}
      />
    </div>
  );
};

export default GaleriePage; 